import React, { useState } from 'react';
import {
    Card,
    Group,
    Title,
    Button,
    Stack,
    Tabs,
    Select,
    Grid,
    Box,
    Text,
    Loader,
    Center,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import {
    IconFileAnalytics,
    IconBuildingStore,
    IconChartLine,
    IconFilter,
} from '@tabler/icons-react';
import { useStatistics } from '../../hooks/use-statistics';
import { StatisticsFilters } from '../../types/statistics-types';
import { RequestType } from '../../types/api';
import { StatisticsSummary } from './components/StatisticsSummary';
import { ClientStatisticsChart } from './components/ClientStatisticsChart';
import { RequestTypeChart } from './components/RequestTypeChart';
import { TimeSeriesChart } from './components/TimeSeriesChart';

export function StatisticsPage() {
    const [filters, setFilters] = useState<StatisticsFilters>({});
    const [showFilters, setShowFilters] = useState(false);

    const { getStatisticsQuery } = useStatistics();
    const statisticsQuery = getStatisticsQuery(filters);

    const handleFilterChange = (key: keyof StatisticsFilters, value: any) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
    };

    const handleResetFilters = () => {
        setFilters({});
    };

    const isLoading = statisticsQuery.isLoading;

    return (
        <Stack gap="md">
            <Card withBorder p="lg">
                <Group justify="space-between" mb="md">
                    <Title order={2}>Statistiche e Report</Title>
                    <Button
                        variant="outline"
                        leftSection={<IconFilter size={16} />}
                        onClick={() => setShowFilters(!showFilters)}
                    >
                        {showFilters ? 'Nascondi filtri' : 'Mostra filtri'}
                    </Button>
                </Group>

                {showFilters && (
                    <Card withBorder shadow="sm" p="md" mb="md">
                        <Stack gap="md">
                            <Title order={4}>Filtri</Title>
                            <Grid>
                                <Grid.Col span={{ base: 12, md: 6 }}>
                                    <DatePickerInput
                                        label="Data inizio"
                                        placeholder="Seleziona data"
                                        value={filters.startDate}
                                        onChange={(date) => handleFilterChange('startDate', date)}
                                        clearable
                                    />
                                </Grid.Col>
                                <Grid.Col span={{ base: 12, md: 6 }}>
                                    <DatePickerInput
                                        label="Data fine"
                                        placeholder="Seleziona data"
                                        value={filters.endDate}
                                        onChange={(date) => handleFilterChange('endDate', date)}
                                        clearable
                                        minDate={filters.startDate}
                                    />
                                </Grid.Col>

                                <Grid.Col span={{ base: 12, md: 6 }}>
                                    <Select
                                        label="Tipo richiesta"
                                        placeholder="Filtra per tipo"
                                        data={[
                                            { value: String(RequestType.Bozzetto), label: 'Bozzetto' },
                                            { value: String(RequestType.Campionatura), label: 'Campionatura' },
                                            { value: String(RequestType.Produzione), label: 'Produzione' },
                                            { value: String(RequestType.Gamma), label: 'Gamma' },
                                        ]}
                                        value={filters.requestType ? String(filters.requestType) : null}
                                        onChange={(value) =>
                                            handleFilterChange(
                                                'requestType',
                                                value ? (Number(value) as RequestType) : undefined
                                            )
                                        }
                                        clearable
                                    />
                                </Grid.Col>

                                <Grid.Col span={12}>
                                    <Group justify="flex-end">
                                        <Button variant="outline" onClick={handleResetFilters}>
                                            Reimposta filtri
                                        </Button>
                                    </Group>
                                </Grid.Col>
                            </Grid>
                        </Stack>
                    </Card>
                )}

                {isLoading ? (
                    <Center p="xl" style={{ minHeight: 200 }}>
                        <Stack align="center">
                            <Loader size="lg" />
                            <Text c="dimmed">Caricamento statistiche...</Text>
                        </Stack>
                    </Center>
                ) : statisticsQuery.data ? (
                    <Box>
                        <StatisticsSummary summary={statisticsQuery.data.summary} />

                        <Tabs defaultValue="time-series" mt="xl">
                            <Tabs.List>
                                <Tabs.Tab value="time-series" leftSection={<IconChartLine size={16} />}>
                                    Andamento temporale
                                </Tabs.Tab>
                                <Tabs.Tab value="clients" leftSection={<IconBuildingStore size={16} />}>
                                    Statistiche clienti
                                </Tabs.Tab>
                                <Tabs.Tab value="request-types" leftSection={<IconFileAnalytics size={16} />}>
                                    Tipi di richieste
                                </Tabs.Tab>
                            </Tabs.List>

                            <Tabs.Panel value="time-series" pt="md">
                                <TimeSeriesChart data={statisticsQuery.data.timeSeries} />
                            </Tabs.Panel>

                            <Tabs.Panel value="clients" pt="md">
                                <ClientStatisticsChart data={statisticsQuery.data.byClient} />
                            </Tabs.Panel>

                            <Tabs.Panel value="request-types" pt="md">
                                <RequestTypeChart data={statisticsQuery.data.byRequestType} />
                            </Tabs.Panel>
                        </Tabs>
                    </Box>
                ) : (
                    <Text c="dimmed" ta="center" p="xl">
                        Nessun dato disponibile. Prova a modificare i filtri.
                    </Text>
                )}
            </Card>
        </Stack>
    );
}
